import React from 'react';
import { useTranslation } from 'react-i18next';
import { GeneratedImage } from './types';

interface ImageResultsDisplayProps {
  generatedImages: GeneratedImage[];
  selectedImageIndex: number;
  setSelectedImageIndex: (index: number) => void;
  onGenerateNewSet: () => void;
  onGenerateVideoFromImage: () => void;
  onUseSelectedImage: () => void;
  disableVideoTab?: boolean;
}

const ImageResultsDisplay: React.FC<ImageResultsDisplayProps> = ({
  generatedImages,
  selectedImageIndex,
  setSelectedImageIndex,
  onGenerateNewSet,
  onGenerateVideoFromImage,
  onUseSelectedImage,
  disableVideoTab = false
}) => {
  const { t } = useTranslation('aiGenerator');

  const selectedImage = generatedImages[selectedImageIndex];
  const isSelectedImageValid = selectedImage?.url && !selectedImage?.error && !selectedImage?.loading;

  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#8b5cf6', fontSize: '18px' }}>
          {t('image.results.chooseTitle')}
        </h3>
        
        {/* Images Grid - 4 slots */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          {generatedImages.map((image, index) => (
            <div
              key={index}
              onClick={() => !image.loading && !image.error && setSelectedImageIndex(index)}
              style={{
                position: 'relative',
                cursor: image.loading || image.error ? 'default' : 'pointer',
                borderRadius: '8px',
                overflow: 'hidden',
                border: selectedImageIndex === index ? '3px solid #8b5cf6' : '2px solid transparent',
                transition: 'all 0.2s ease',
                transform: selectedImageIndex === index ? 'scale(1.02)' : 'scale(1)',
                minHeight: '200px',
                backgroundColor: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* Provider Label */}
              <div style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                backgroundColor: image.provider === 'runway' ? '#8b5cf6' : '#3b82f6',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                zIndex: 2
              }}>
                {image.provider === 'runway' ? '⚡' : '🛩️'}
              </div>

              {/* Loading State */}
              {image.loading && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#ccc'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid rgba(139, 92, 246, 0.3)',
                    borderTop: '3px solid #8b5cf6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <div style={{ fontSize: '14px', textAlign: 'center' }}>
                    {t('image.results.generating')}
                  </div>
                </div>
              )}

              {/* Error State */}
              {image.error && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  color: '#ef4444',
                  padding: '20px'
                }}>
                  <div style={{ 
                    fontSize: '48px',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                  }}>
                    ❌
                  </div>
                  <div style={{ fontSize: '16px', textAlign: 'center', fontWeight: 'bold' }}>
                    {t('image.results.generationFailed')}
                  </div>
                  {image.errorMessage && (
                    <div style={{ 
                      fontSize: '12px', 
                      textAlign: 'center', 
                      color: '#fca5a5',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      maxWidth: '90%'
                    }}>
                      {image.errorMessage}
                    </div>
                  )}
                </div>
              )}

              {/* Generated Image */}
              {image.url && !image.error && !image.loading && (
                <>
                  <img 
                    src={image.url}
                    alt={t('image.results.generatedOption', { number: index + 1 })}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  
                  {/* Selection Indicator */}
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: selectedImageIndex === index ? '#8b5cf6' : 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: 'white',
                    zIndex: 2
                  }}>
                    {selectedImageIndex === index ? '✓' : index + 1}
                  </div>
                  
                  {/* Seed Info */}
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '8px',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 2
                  }}>
                    {t('image.results.seed')}: {image.seed}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Selected Image Details */}
        <div style={{ backgroundColor: '#1a1a1a', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#8b5cf6', fontSize: '16px' }}>
            {t('image.results.selectedDetails')}
          </h4>
          <p style={{ margin: '5px 0', fontSize: '14px', color: '#ccc' }}>
            <strong>{t('image.results.option')}:</strong> {selectedImageIndex + 1} {t('image.results.of')} {generatedImages.length}
          </p>
          <p style={{ margin: '5px 0', fontSize: '14px', color: '#ccc' }}>
            <strong>{t('image.results.provider')}:</strong> {selectedImage.provider === 'runway' ? `🛩️ ${t('image.results.premium')}` : `⚡ ${t('image.results.fast')}`}
          </p>
          <p style={{ margin: '5px 0', fontSize: '14px', color: '#ccc' }}>
            <strong>{t('image.results.prompt')}:</strong> {selectedImage.prompt}
          </p>
          <p style={{ margin: '5px 0', fontSize: '14px', color: '#ccc' }}>
            <strong>{t('image.results.seed')}:</strong> {selectedImage.seed}
          </p>
          {selectedImage.loading && (
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#f59e0b' }}>
              <strong>{t('image.results.status')}:</strong> {t('image.results.generating')}
            </p>
          )}
          {selectedImage.error && (
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#ef4444' }}>
              <strong>{t('image.results.status')}:</strong> {t('image.results.generationFailed')} - {selectedImage.errorMessage || t('image.results.unknownError')}
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          onClick={onGenerateNewSet}
          style={{
            padding: '12px 24px',
            border: '1px solid rgba(255,255,255,0.2)',
            backgroundColor: 'transparent',
            color: 'white',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {t('image.results.generateNewSet')}
        </button>
        
        {!disableVideoTab && (
          <button
            onClick={onGenerateVideoFromImage}
            disabled={!isSelectedImageValid}
            style={{
              padding: '12px 24px',
              background: !isSelectedImageValid ? '#666' : 'linear-gradient(135deg, #8b5cf6, #e11d48)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: !isSelectedImageValid ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {t('image.results.generateVideoFromImage')}
          </button>
        )}
        
        <button
          onClick={onUseSelectedImage}
          disabled={!isSelectedImageValid}
          style={{
            padding: '12px 24px',
            background: !isSelectedImageValid ? '#666' : 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: !isSelectedImageValid ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {t('image.results.useSelectedImage')}
        </button>
      </div>
    </>
  );
};

export default ImageResultsDisplay;